/**
 * CSS Manager - Dynamic CSS loading with prefetching and caching
 */

class CSSManager {
  constructor() {
    this.loadedStyles = new Set();
    this.loadingPromises = new Map();
    this.prefetchedPages = new Set();
  }

  /** Load CSS for a specific page with caching and deduplication */
  async loadPageCSS(pageName) {
    if (this.loadedStyles.has(pageName)) return;
    if (this.loadingPromises.has(pageName)) {
      return this.loadingPromises.get(pageName);
    }

    const loadPromise = this._loadCSSModule(pageName);
    this.loadingPromises.set(pageName, loadPromise);

    try {
      await loadPromise;
      this.loadedStyles.add(pageName);
    } catch (error) {
      // silent
    } finally {
      this.loadingPromises.delete(pageName);
    }
  }

  /** @private */
  async _loadCSSModule(pageName) {
    switch (pageName) {
      case 'dashboard': return; // Loaded globally in main.js
      case 'landing': return import('../styles/landing.css');
      case 'login': return import('../styles/login.css');
      case 'clients': return import('../styles/clients.css');
      case 'client-profile': return import('../styles/client-profile.css');
      case 'client-login': return import('../styles/client-login.css');
      case 'settings': return import('../styles/settings.css');
      case 'subscription': return import('../styles/subscription.css');
      case 'checkout': return import('../styles/checkout.css');
      case 'legal': return import('../styles/legal.css');
      case 'admin-dashboard': return import('../styles/admin-dashboard.css');
      default: return;
    }
  }

  /** Prefetch CSS for a page in background */
  prefetchPageCSS(pageName) {
    if (this.loadedStyles.has(pageName) || this.prefetchedPages.has(pageName)) return;

    this.prefetchedPages.add(pageName);
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.loadPageCSS(pageName));
    } else {
      setTimeout(() => this.loadPageCSS(pageName), 100);
    }
  }

  /** Prefetch CSS for likely next pages based on current page */
  prefetchLikelyPages(currentPage) {
    const prefetchMap = {
      'landing': ['login'],
      'login': ['dashboard'],
      'dashboard': ['clients', 'settings'],
      'clients': ['client-profile', 'client-login'],
      'client-profile': ['clients'],
      'settings': ['dashboard']
    };

    (prefetchMap[currentPage] || []).forEach(page => this.prefetchPageCSS(page));
  }
}

export const cssManager = new CSSManager();